<template>
  <div ref="scrollElement" class="page-academy wh-full overflow-auto">
    <Banner :pageBackground="pageBackground" :pageTitle="pageTitle" :pageSubTitle="pageSubTitle" />
    <div :class="'sticky-active'">
      <XTab v-model="currentVisible" :items="displayCategory" @tab-click="handleScrollTo" />
    </div>
    <article class="page-academy-content mx-auto">
      <section
        v-for="item in displayCategory"
        :id="item.title"
        :key="item.id"
        :class="['page-academy-content__group mb-[28px]', item.jump_url.replaceAll('/', '')]"
      >
        <component :is="displayCard(item)" :isAcademy="true" :category="item"></component>
      </section>
    </article>
  </div>
</template>

<script lang="ts" setup>
import { debounce } from 'lodash-es';
import { PAGE_CARD_TYPE } from '~/api/interface/publicize';
import { SearchModelType } from '~/api/interface/search';
import { getHelpCategory, type IHelpCategory } from '~/api/module/help-center';
import { getPromotionCenterPageCard } from '~/api/module/publicize';
import XTab from '~/design/atom/tab.vue';
import { useWatchPageSeoData } from '~/hooks/useSeo';
import Banner from './banner.vue';
import CategoryBrand from './category-brand.vue';
import CategoryDoc from './category-doc.vue';
import CategoryHighlight from './category-highlight.vue';
import CategoryLive from './category-live.vue';
import CategoryTutorial from './category-tutorial.vue';

const currentVisible = ref<number>(0);
const isSeek = ref(false);

const onScroll = debounce(() => {
  if (isSeek.value) {
    isSeek.value = false;
    return;
  }
  const list: number[] = [];
  document.querySelectorAll<HTMLDivElement>('.page-academy-content__group').forEach(item => {
    list.push(item.getBoundingClientRect().top - 170);
  });

  const targetIndex = list.findIndex(item => item > 0);

  currentVisible.value = targetIndex < 1 ? 0 : targetIndex - 1;
}, 300);

const handleScrollTo = (idx: number, item?: any) => {
  const list = document.querySelectorAll<HTMLDivElement>('.page-academy-content__group');

  list[idx].style.scrollMarginTop = '100px';
  list[idx].scrollIntoView({ behavior: 'smooth' });
  currentVisible.value = idx;
  isSeek.value = true;
};

onMounted(() => {
  scrollElement.value?.addEventListener('scroll', onScroll, { passive: true });
});

onBeforeUnmount(() => {
  scrollElement.value?.removeEventListener('scroll', onScroll);
});

const scrollElement = ref<HTMLDivElement>();

const displayCard = (data: IHelpCategory) => {
  if (data.column_type === 2) {
    return CategoryLive;
  }
  switch (data.jump_url) {
    case '/case':
    case 'case':
      return CategoryBrand;
    case 'tutorial':
    case '/tutorial':
      return CategoryTutorial;
    case 'highlight':
    case '/highlight':
      return CategoryHighlight;
    default:
      return CategoryDoc;
  }
};

const { data: categoryData } = useAsyncData(async () => {
  const res = await getHelpCategory({ model_type: SearchModelType.ACADEMY });
  console.log('res ==========>', res);
  return res.data;
});

const { data: pageData } = useAsyncData(async () => {
  const res = await getPromotionCenterPageCard({
    type: PAGE_CARD_TYPE.ACADEMY,
  });
  return res.data?.[0];
});

const displayCategory = computed(() => {
  return (
    categoryData.value
      ?.filter(item => (item.magic_school_detail || [])?.length > 0 || item.jump_url)
      .map(item => ({
        ...item,
        magic_school_detail: (item.magic_school_detail || []).sort((a, b) => {
          if (a.rank !== b.rank) {
            return b.rank - a.rank;
          }
          // 如果 rank 相同，则按 online_time 降序排序
          return new Date(b?.online_time).getTime() - new Date(a?.online_time).getTime();
        }),
      })) || []
  );
});

const pageTitle = computed(() => pageData.value?.title);
const pageSubTitle = computed(() => pageData.value?.sub_title);
const pageBackground = computed(() => pageData.value?.cover_url);

useWatchPageSeoData(pageData);
</script>

<style lang="scss" scoped>
.page-academy-content__group {
  &.tutorial {
    margin-bottom: 40px;
  }
  &.highlight {
    margin-bottom: 40px;
  }
}

:deep(h2) {
  margin-top: 0;
}

.sticky-hidden {
  position: absolute;
  transform: translateY(-100%);
  transition: all 0.3s ease-out;
}
.sticky-active {
  position: sticky;
  top: 40px;
  transform: translateY(0%);
  transition: all 0.3s ease-out;
  width: 100%;
  margin: 40px auto;
  height: 48px;
  pointer-events: none;
  z-index: var(--layer-sticky);
  display: flex;
  justify-content: center;
}
.page-academy {
  height: calc(100vh - 56px);
  display: block !important;
  &-content {
    width: 1200px;
    padding: 0 0 40px;
  }
}
</style>
